// Utilitaires pour sauvegarder et restaurer l'état Redux dans le localStorage

// Clé pour stocker les coûts dans le localStorage
const COSTS_STORAGE_KEY = "alpheys_costs_state";

import { CostsState } from "./types";
import { InventoryDraftState } from "./inventoryDraftSlice";
import { StoreRegulationState } from "./storeRegulationSlice";
import { CashFlowState } from "./cashFlowSlice";

// Clé pour stocker les brouillons d'inventaire (inventory/entry/exit)
const INVENTORY_DRAFTS_STORAGE_KEY = "alpheys_inventory_drafts_state";
// Clé pour stocker les templates de régulation des boutiques
const STORE_REGULATION_STORAGE_KEY = "alpheys_store_regulation_state";
// Clé pour stocker l'état du cash flow (solde initial + courant)
const CASHFLOW_STORAGE_KEY = "alpheys_cashflow_state";
const HOT_MEMORY_STORAGE_KEY = "alpheys_hot_memory_state";
const HARVEN_CHAT_STORAGE_KEY = "alpheys_harven_chat_state";
const SUPPLY_NOTIFICATIONS_STORAGE_KEY = "alpheys_supply_notifications_state";
const SUPPLY_NOTIFICATIONS_TTL_MS = 10 * 24 * 60 * 60 * 1000;

const pruneSupplyNotifications = (state: any) => {
  if (!state || !Array.isArray(state.items)) return state;
  const now = Date.now();
  const items = state.items.filter((item: any) => {
    const addedAt = item?.addedAt ? new Date(item.addedAt).getTime() : 0;
    return addedAt && now - addedAt <= SUPPLY_NOTIFICATIONS_TTL_MS;
  });
  return { ...state, items };
};

// Fonction pour sauvegarder l'u00e9tat des cou00fbts dans le localStorage
export const saveCostsToLocalStorage = (costsState: CostsState) => {
  try {
    const serializedState = JSON.stringify(costsState);
    localStorage.setItem(COSTS_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde des coûts dans le localStorage:",
      error
    );
  }
};

export const saveSupplyNotificationsToLocalStorage = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(SUPPLY_NOTIFICATIONS_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde des notifications supply dans le localStorage:",
      error
    );
  }
};

export const loadSupplyNotificationsFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(SUPPLY_NOTIFICATIONS_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    const parsed = JSON.parse(serializedState);
    return pruneSupplyNotifications(parsed);
  } catch (error) {
    console.error(
      "Erreur lors du chargement des notifications supply depuis le localStorage:",
      error
    );
    return undefined;
  }
};

export const saveHarvenChatToLocalStorage = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(HARVEN_CHAT_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde de Harven Chat dans le localStorage:",
      error
    );
  }
};

export const loadHarvenChatFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(HARVEN_CHAT_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error(
      "Erreur lors du chargement de Harven Chat depuis le localStorage:",
      error
    );
    return undefined;
  }
};

export const saveStoreRegulationToLocalStorage = (
  state: StoreRegulationState
) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STORE_REGULATION_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde de la régulation des boutiques dans le localStorage:",
      error
    );
  }
};

export const loadStoreRegulationFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(STORE_REGULATION_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState) as StoreRegulationState;
  } catch (error) {
    console.error(
      "Erreur lors du chargement de la régulation des boutiques depuis le localStorage:",
      error
    );
    return undefined;
  }
};

export const saveHotMemoryToLocalStorage = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(HOT_MEMORY_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde du hot memory dans le localStorage:",
      error
    );
  }
};

export const loadHotMemoryFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(HOT_MEMORY_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error(
      "Erreur lors du chargement du hot memory depuis le localStorage:",
      error
    );
    return undefined;
  }
};

// Fonction pour charger l'u00e9tat des cou00fbts depuis le localStorage
export const loadCostsFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(COSTS_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error(
      "Erreur lors du chargement des coûts depuis le localStorage:",
      error
    );
    return undefined;
  }
};

// Sauvegarder l'état des brouillons d'inventaire dans le localStorage
export const saveInventoryDraftsToLocalStorage = (
  draftsState: InventoryDraftState
) => {
  try {
    const serializedState = JSON.stringify(draftsState);
    localStorage.setItem(INVENTORY_DRAFTS_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde des brouillons d'inventaire dans le localStorage:",
      error
    );
  }
};

// Charger l'état des brouillons d'inventaire depuis le localStorage
export const loadInventoryDraftsFromLocalStorage = () => {
  try {
    const serializedState = localStorage.getItem(INVENTORY_DRAFTS_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    const parsed = JSON.parse(serializedState);

    // Migration: ancienne structure {inventory: DraftPayload, entry: DraftPayload, exit: DraftPayload}
    // vers nouvelle structure {inventory: Record<location, DraftPayload>, ...}
    const looksLikeOldShape =
      parsed && parsed.inventory && Array.isArray(parsed.inventory.changes);

    if (looksLikeOldShape) {
      const migrateMode = (modeKey: "inventory" | "entry" | "exit" | "transfer") => {
        const payload = parsed[modeKey];
        if (payload && Array.isArray(payload.changes)) {
          const loc = payload.location || "__unknown_location__";
          return { [loc]: payload };
        }
        return {};
      };

      const migrated: InventoryDraftState = {
        inventory: migrateMode("inventory"),
        entry: migrateMode("entry"),
        exit: migrateMode("exit"),
        transfer: migrateMode("transfer"),
      };
      return migrated;
    }

    return parsed as InventoryDraftState;
  } catch (error) {
    console.error(
      "Erreur lors du chargement des brouillons d'inventaire depuis le localStorage:",
      error
    );
    return undefined;
  }
};

// Sauvegarder l'état du cash flow (solde initial + courant) dans le localStorage
export const saveCashFlowToLocalStorage = (state: CashFlowState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(CASHFLOW_STORAGE_KEY, serializedState);
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde du cash flow dans le localStorage:",
      error
    );
  }
};

// Charger l'état du cash flow depuis le localStorage
export const loadCashFlowFromLocalStorage = (): CashFlowState | undefined => {
  try {
    const serializedState = localStorage.getItem(CASHFLOW_STORAGE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState) as CashFlowState;
  } catch (error) {
    console.error(
      "Erreur lors du chargement du cash flow depuis le localStorage:",
      error
    );
    return undefined;
  }
};
